import { useEffect, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { motion } from "framer-motion";
import { toast } from "sonner";
import api, { apiErr, BACKEND_URL } from "@/lib/api";
import { startVoice } from "@/lib/realtimeVoice";
import { AvaHiveOrb } from "@/components/ui/AvaHiveOrb";
import { OctonLogo } from "@/components/ui/OctonLogo";
import { GenomePanel } from "@/components/ui/GenomePanel";
import { SleepReport } from "@/components/ui/SleepReport";
import { PRCelebration } from "@/components/ui/PRCelebration";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Send, Moon, Radio, Mic } from "lucide-react";
import { IMG } from "@/lib/assets";
import { CoachAvatar } from "@/components/ui/CoachAvatar";
import { AREAS } from "@/lib/areas";

const STARTERS = [
  "What should I train today?",
  "I did 5x5 squats at 80kg",
  "Build me a 4-week strength plan",
  "How am I progressing?",
  "Tips to fix my deadlift form",
  "What should I eat after training?",
];

export default function Coach({ clientId }) {
  const qc = useQueryClient();
  const params = clientId ? { client_id: clientId } : {};
  const [input, setInput] = useState("");
  const [urlParams] = useSearchParams();
  const wantsProgress = urlParams.get("topic") === "progress";
  const areaTopic = AREAS.find((a) => a.key === urlParams.get("topic"));
  useEffect(() => { if (areaTopic) setInput(`Help me improve my ${areaTopic.title.toLowerCase()}. What should I focus on next?`); }, []); // eslint-disable-line
  const [sending, setSending] = useState(false);
  const [messages, setMessages] = useState([]);
  const [voiceStatus, setVoiceStatus] = useState("idle");
  const [speaking, setSpeaking] = useState(false);
  const [sleepReport, setSleepReport] = useState(null);
  const [sleepOpen, setSleepOpen] = useState(false);
  const [sleeping, setSleeping] = useState(false);
  const [prCelebration, setPrCelebration] = useState(null);
  const voiceRef = useRef(null);
  const scrollRef = useRef(null);
  const speakTimer = useRef(null);
  const narrationAudio = useRef(null);

  const { data: genome } = useQuery({
    queryKey: ["genome", clientId],
    queryFn: async () => (await api.get("/api/ava/genome", { params })).data,
  });

  useEffect(() => {
    api.get("/api/ava/messages", { params }).then((r) => setMessages(r.data)).catch(() => {});
    // eslint-disable-next-line
  }, [clientId]);

  useEffect(() => { scrollRef.current?.scrollTo(0, scrollRef.current.scrollHeight); }, [messages]);
  useEffect(() => () => { voiceRef.current?.stop(); narrationAudio.current?.pause(); }, []);

  const prDetail = (l) => {
    const bits = [];
    if (l.weight_kg != null) bits.push(`${l.weight_kg}kg`);
    if (l.reps != null) bits.push(`${l.sets ? l.sets + "x" : ""}${l.reps}`);
    if (l.score) bits.push(l.score);
    if (l.distance_m != null) bits.push(`${l.distance_m}m`);
    return bits.join(" · ");
  };
  const celebrateIfPR = (logged) => {
    if (clientId) return; // only celebrate the athlete's own PRs, not a coach viewing them
    const pr = (logged || []).find((l) => l.is_pr);
    if (pr) setPrCelebration({ movement: pr.movement, detail: prDetail(pr) });
  };

  const send = async (e, preset) => {
    e?.preventDefault();
    const msg = (preset ?? input).trim();
    if (!msg || sending) return;
    setInput("");
    setMessages((m) => [...m, { role: "user", content: msg, id: Date.now() }]);
    setSending(true);
    try {
      const { data } = await api.post("/api/ava/chat", { message: msg, client_id: clientId });
      setMessages((m) => [...m, { role: "assistant", content: data.reply, id: Date.now() + 1 }]);
      if (data.logged?.length) {
        const names = data.logged.map((l) => l.movement + (l.is_pr ? " 🏆" : "")).join(", ");
        toast.success(`OCTON logged: ${names}`);
        qc.invalidateQueries({ queryKey: ["prs"] });
        qc.invalidateQueries({ queryKey: ["liftlogs"] });
        qc.invalidateQueries({ queryKey: ["insights"] });
        celebrateIfPR(data.logged);
      }
    } catch (err) {
      toast.error(apiErr(err));
    } finally { setSending(false); }
  };

  const toggleVoice = async (topicArg) => {
    if (voiceRef.current) { voiceRef.current.stop(); voiceRef.current = null; return; }
    try {
      voiceRef.current = await startVoice({
        clientId,
        topic: typeof topicArg === "string" ? topicArg : undefined,
        onStatus: setVoiceStatus,
        onEvent: (ev) => {
          const t = ev.type || "";
          if (t.includes("audio") && t.includes("delta")) {
            setSpeaking(true);
            clearTimeout(speakTimer.current);
            speakTimer.current = setTimeout(() => setSpeaking(false), 500);
          }
          if (t === "response.done" || t === "output_audio_buffer.stopped") setSpeaking(false);
          // Live transcription -> visible reflective conversation
          if (t === "conversation.item.input_audio_transcription.completed" && ev.transcript) {
            const spoken = ev.transcript.trim();
            setMessages((m) => [...m, { role: "user", content: spoken, id: `v${Date.now()}` }]);
            // OCTON auto-logs any lifts mentioned out loud
            api.post("/api/logs/extract", { text: spoken, source: "voice", client_id: clientId })
              .then(({ data }) => {
                if (data.logs?.length) {
                  const names = data.logs.map((l) => l.movement + (l.is_pr ? " 🏆" : "")).join(", ");
                  toast.success(`OCTON logged: ${names}`);
                  celebrateIfPR(data.logs);
                }
              })
              .catch(() => {});
          }
          if ((t === "response.audio_transcript.done" || t === "response.output_audio_transcript.done") && ev.transcript) {
            setMessages((m) => [...m, { role: "assistant", content: ev.transcript.trim(), id: `a${Date.now()}` }]);
          }
        },
      });
    } catch (err) {
      toast.error(apiErr(err, "Could not start voice. Allow microphone access."));
      setVoiceStatus("idle");
      voiceRef.current = null;
    }
  };

  const runSleep = async () => {
    setSleeping(true);
    try {
      const { data } = await api.post("/api/ava/sleep", null, { params });
      setSleepReport(data);
      setSleepOpen(true);
      qc.invalidateQueries({ queryKey: ["genome", clientId] });
      toast.success(`Sleep cycle #${data.cycle} complete`);
      playNarration(data.narration); // OCTON reads it aloud automatically
    } catch (err) {
      toast.error(apiErr(err));
    } finally { setSleeping(false); }
  };

  const playNarration = async (text) => {
    if (!text) return;
    try {
      narrationAudio.current?.pause();
      const res = await fetch(`${BACKEND_URL}/api/ava/narrate`, {
        method: "POST", credentials: "include",
        headers: { "Content-Type": "application/json" }, body: JSON.stringify({ text }),
      });
      if (!res.ok) throw new Error("tts");
      const blob = await res.blob();
      const audio = new Audio(URL.createObjectURL(blob));
      narrationAudio.current = audio;
      audio.play();
    } catch (_) {
      if ("speechSynthesis" in window) {
        const u = new SpeechSynthesisUtterance(text);
        window.speechSynthesis.speak(u);
      }
    }
  };

  const narrateSleep = () => playNarration(sleepReport?.narration);

  return (
    <div className="p-4 md:p-8 grid grid-cols-1 lg:grid-cols-12 gap-6">
      {/* Main stage */}
      <div className="lg:col-span-8 space-y-6">
        <div className="relative overflow-hidden flex flex-col sm:flex-row items-center gap-6 rounded-xl border border-[#1E222A] p-6">
          <img src={IMG.strength} alt="" className="absolute inset-0 w-full h-full object-cover opacity-35" />
          <div className="absolute inset-0" style={{ background: "linear-gradient(90deg,rgba(5,5,5,0.55),#050505 75%)" }} />
          <div className="relative"><AvaHiveOrb status={voiceStatus} speaking={speaking} predictionError={genome?.prediction_error ?? 0.3}
            size={160} onClick={() => toggleVoice()} /></div>
          <div className="relative flex-1 text-center sm:text-left w-full">
            <span className="mono-label inline-flex items-center gap-2 justify-center sm:justify-start"
              style={{ color: voiceStatus === "live" ? "#CCFF00" : "#565C6F" }}>
              <Radio className="w-3.5 h-3.5" /> {voiceStatus === "live" ? "LIVE · listening" : "voice standby"}
            </span>
            <h1 className="font-heading font-black text-2xl md:text-3xl tracking-tight mt-2">Your OCTON Coach</h1>
            <p className="text-[#8A91A6] text-sm mt-1 max-w-md">
              Tap the orb to talk live, or type below. Your coach remembers how you train and builds on it.
            </p>

            {/* Mothership link to OCTON super-brain */}
            <div className="mt-4 surface-elev rounded-md p-3 flex items-center gap-3" data-testid="mothership-link">
              <div className="relative w-8 h-8 shrink-0">
                <OctonLogo size={32} glow={voiceStatus === "live"} />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <span className="mono-label" style={{ color: voiceStatus === "live" ? "#CCFF00" : "#565C6F" }}>
                    {voiceStatus === "live" ? "LINKED · OCTON MOTHERSHIP" : "MOTHERSHIP · STANDBY"}
                  </span>
                </div>
                <div className="relative h-1 mt-2 bg-[#05060a] rounded-full overflow-hidden">
                  <motion.div className="absolute top-0 h-full w-1/3 rounded-full"
                    style={{ background: "linear-gradient(90deg,transparent,#CCFF00,transparent)" }}
                    animate={{ x: voiceStatus === "live" ? ["-40%", "340%"] : "0%" }}
                    transition={{ duration: 1.6, repeat: Infinity, ease: "linear" }} />
                </div>
                <div className="flex gap-4 mt-2 mono-label">
                  <span>{genome?.concepts?.length ?? 0} concepts</span>
                  <span style={{ color: (genome?.prediction_error ?? 0) > 0.55 ? "#FF3B30" : "#8A91A6" }}>
                    PE {genome?.prediction_error ?? "0.00"}
                  </span>
                  <span>{genome?.cycles ?? 0} cycles</span>
                </div>
              </div>
            </div>

            {wantsProgress && voiceStatus !== "live" && (
              <p className="mt-4 text-sm text-acid animate-pulse">Tap "Review my progress" to start your voice review.</p>
            )}
            <div className="mt-4 flex flex-wrap gap-2 justify-center sm:justify-start">
              <Button onClick={() => toggleVoice(voiceStatus === "live" ? undefined : "progress")} data-testid="voice-progress-btn"
                className={`rounded-full h-10 px-5 font-medium ${voiceStatus === "live" ? "bg-white/10 text-white hover:bg-white/20" : "bg-acid text-black hover:bg-[#AACC00]"} ${wantsProgress && voiceStatus !== "live" ? "ring-2 ring-acid ring-offset-2 ring-offset-black" : ""}`}>
                <Mic className="w-4 h-4" /> {voiceStatus === "live" ? "End voice session" : "Review my progress"}
              </Button>
              <Button onClick={runSleep} disabled={sleeping} data-testid="run-sleep-btn"
                className="bg-[#FFB340] text-black hover:bg-[#ffc35f] rounded-full h-10 px-5 font-medium">
                <Moon className="w-4 h-4" /> {sleeping ? "Reviewing…" : "Run overnight review now"}
              </Button>
            </div>
          </div>
        </div>

        {/* Chat */}
        <div className="rounded-xl border border-[#1E222A] bg-gradient-to-b from-[#0F1115] to-[#0B0D11] flex flex-col h-[56vh]">
          <div ref={scrollRef} className="flex-1 overflow-y-auto p-5 space-y-4" data-testid="chat-scroll">
            {messages.length === 0 && (
              <div className="h-full flex flex-col items-center justify-center text-center">
                <CoachAvatar size={64} />
                <p className="font-heading font-bold text-lg mt-3">Hi, I'm your OCTON Coach.</p>
                <p className="text-sm text-[#8A91A6] max-w-sm mt-1">Tell me what you trained and I'll log it, or ask me anything about training, form, recovery or food.</p>
                <div className="flex flex-wrap justify-center gap-2 mt-5 max-w-lg">
                  {STARTERS.map((q) => (
                    <button key={q} type="button" onClick={() => send(null, q)} data-testid="chat-starter"
                      className="text-sm rounded-full px-3.5 py-1.5 border border-[#343A4A] text-white/85 hover:border-acid hover:text-acid transition-colors">{q}</button>
                  ))}
                </div>
              </div>
            )}
            {messages.map((m) => (
              <motion.div key={m.id} initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }}
                className={`flex items-end gap-2 ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                {m.role !== "user" && <CoachAvatar size={28} ring={false} />}
                <div className={`max-w-[80%] rounded-lg px-4 py-2.5 text-sm leading-relaxed ${
                  m.role === "user" ? "bg-acid text-black" : "surface-elev text-white/90"
                }`} data-testid={`msg-${m.role}`}>
                  {m.content}
                </div>
              </motion.div>
            ))}
            {sending && <div className="flex items-center gap-2"><CoachAvatar size={28} ring={false} /><p className="mono-label animate-pulse">Your coach is thinking…</p></div>}
          </div>
          <form onSubmit={send} className="p-3 border-t border-[#1E222A] flex gap-2">
            <Input data-testid="chat-input" value={input} onChange={(e) => setInput(e.target.value)}
              placeholder="Message your OCTON Coach…" className="bg-[#0F1115] border-[#1E222A] h-11" />
            <Button type="submit" disabled={sending} data-testid="chat-send-btn"
              className="bg-acid text-black hover:bg-[#AACC00] h-11 w-11 p-0 rounded-md shrink-0">
              <Send className="w-4 h-4" />
            </Button>
          </form>
        </div>
      </div>

      {/* Right rail - genome */}
      <div className="lg:col-span-4">
        <GenomePanel genome={genome} />
      </div>

      <SleepReport open={sleepOpen} onOpenChange={setSleepOpen} report={sleepReport} onNarrate={narrateSleep} />
      <PRCelebration pr={prCelebration} onClose={() => setPrCelebration(null)} />
    </div>
  );
}
